import React, { memo, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { WheelData } from '../WheelOfFortune/types';
import { WHEEL_PALETTES, WheelPalette } from '../wheelTheme';
import { drawWheel, DrawWheelProps } from './WheelOfFortuneCanvas.utils';

interface WheelCanvasProps extends DrawWheelProps {
  width: string;
  height: string;
  data: WheelData[];
  variant?: 'default' | 'dark' | 'vip';
  palette?: WheelPalette;
}

const WheelOfFortuneCanvas = ({
  width,
  height,
  data,
  variant = 'default',
  palette = WHEEL_PALETTES.regular,
  ...drawWheelProps
}: WheelCanvasProps): React.ReactElement => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDarkVariant = variant === 'dark';

  useEffect(() => {
    if (!canvasRef.current) return;

    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled && canvasRef.current) {
        drawWheel(canvasRef, data, { ...drawWheelProps, palette });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    data,
    drawWheelProps.fontWeight,
    drawWheelProps.fontSize,
    drawWheelProps.fontStyle,
    drawWheelProps.prizeMap,
    drawWheelProps.rouletteUpdater,
    drawWheelProps.textDistance,
    drawWheelProps.prizeNumber,
    drawWheelProps.hasStoppedSpinning,
    drawWheelProps.hasStartedSpinning,
    // Repaint on theme change: the palette decides every fill, so switching wheels
    // without this leaves the previous wheel's colours on the canvas.
    palette,
  ]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={twMerge(
        'tw-z-10 tw-h-[94%] tw-w-[94%] tw-rounded-full',
        isDarkVariant ? 'tw-bg-[#212121]' : palette.canvasBg
      )}
    />
  );
};

export default memo(WheelOfFortuneCanvas);
